import { type Dispatch, type SetStateAction } from "react";
import { type LatLng, type LeafletMouseEvent, type Marker } from "leaflet";
import { type MarkerState } from "../types";
import { dispatchMapClickTagUpdate } from "../utils/tagSync/mapClickTagSync";
import { dispatchMapClickRemarksUpdate } from "../utils/tagSync/mapClickRemarksSync";
import { dispatchMapClickAdditionalFieldsUpdate } from "../utils/tagSync/mapClickAdditionalFieldsSync";
import {
  setActivePopup,
  getActivePopup,
} from "../utils/tagSync/activePopupManager";

/**
 * マーカーのイベントハンドラーを生成する
 */
export const createMarkerEventHandlers = (
  markerState: MarkerState,
  setClickedLatLng: Dispatch<SetStateAction<LatLng | null>>,
  onMapClickTagsUpdate: (() => string[]) | undefined,
  onMapClickRemarksUpdate: (() => string) | undefined,
  onMapClickAdditionalFieldsUpdate: (() => string) | undefined,
  currentPopupIdentifier:
    | { location: string; chiban: string; prefName: string }
    | null
    | undefined
): {
  click: (event: LeafletMouseEvent) => void;
  popupopen: () => void;
  popupclose: () => void;
} => ({
  click: (event: LeafletMouseEvent) => {
    if (!markerState.content) {
      (event.target as Marker).closePopup();
    }
    // 既存の青ピン(地図クリック時に出るピン)をクリックした場合もポップアップ内容を更新するため、
    // clickedLatLngを再設定する。これにより、ChibanViewerのuseEffectが実行され、最新の青ピン専用ハンドラーでポップアップが再生成される。
    setClickedLatLng(markerState.latlng);
  },
  popupopen: () => {
    // ポップアップが開かれた際に最新のタグ、備考、追加項目を取得してPopupContentに送信
    // PropertySelectionGridでタグ、備考、追加項目が変更されても、ポップアップを開き直すと最新の状態が反映される
    if (onMapClickTagsUpdate) {
      const currentTags = onMapClickTagsUpdate();
      dispatchMapClickTagUpdate(currentTags);
    }

    if (onMapClickRemarksUpdate) {
      const currentRemarks = onMapClickRemarksUpdate();
      dispatchMapClickRemarksUpdate(currentRemarks);
    }

    if (onMapClickAdditionalFieldsUpdate) {
      const currentAdditionalFields = onMapClickAdditionalFieldsUpdate();
      dispatchMapClickAdditionalFieldsUpdate(currentAdditionalFields);
    }

    // アクティブなポップアップとして登録
    // currentPopupIdentifierに必要な情報がすべて含まれている場合は、それを直接使用
    // ただし、既に同じポップアップが開いている場合は更新しない
    if (currentPopupIdentifier) {
      const existingActivePopup = getActivePopup();
      const isSamePopup =
        existingActivePopup &&
        existingActivePopup.location === currentPopupIdentifier.location &&
        existingActivePopup.chiban === currentPopupIdentifier.chiban &&
        existingActivePopup.prefName === currentPopupIdentifier.prefName &&
        existingActivePopup.pinType === "map-click";

      if (!isSamePopup) {
        setActivePopup({
          location: currentPopupIdentifier.location,
          chiban: currentPopupIdentifier.chiban,
          prefName: currentPopupIdentifier.prefName,
          pinType: "map-click",
        });
      }
    }
  },
  popupclose: () => {
    // ポップアップが閉じられたときにアクティブポップアップをクリア
    setActivePopup(null);
  },
});
